import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PatientImage, ReportDto, ReportRequest } from '@interfaces/report';
import { EnvironmentService } from '@services/enviroment/environment.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  reports: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  reportsObservable$ = this.reports.asObservable();

  requests: BehaviorSubject<ReportRequest[]> = new BehaviorSubject<ReportRequest[]>([]);
  requestsObservable$ = this.requests.asObservable();

  protected readonly http = inject(HttpClient);
  private environmentService = inject(EnvironmentService);

  apiUrl = this.environmentService.getValue('apiUrl');

  fetchReportRequests(): Observable<ReportRequest[]> {
    return this.http.get(this.apiUrl + '/report_requests',
      {})
      .pipe(map((res: any) => res.report_requests));
  }

  fetchSurveyForPatient(patientId: string): Observable<any> {
    return this.http.get(this.apiUrl + '/surveys/' + patientId);
  }

  updateRequestsList(requests: ReportRequest[]): void {
    this.requests.next(requests);
  }

  markReportRequestInProgress(requestId: string): Observable<any> {
    return this.http.patch(this.apiUrl + '/report_requests/' + requestId + '/in_progress', {});
  }

  markReportRequestCompleted(requestId: string): Observable<any> {
    return this.http.patch(this.apiUrl + '/report_requests/' + requestId + '/completed', {});
  }

  createReportRequest(doctorId: string, patientId: string): Observable<any> {
    return this.http.post(this.apiUrl + '/report_requests', {
      requested_by: doctorId,
      requested_for: patientId
    });
  }

  createReport(request: ReportDto): Observable<any> {
    return this.http.post(this.apiUrl + '/reports', request);
  }

  fetchReportDetail(doctorId: string, patientId: string): Observable<any> {
    return this.http.get(this.apiUrl + '/report/' + patientId).pipe(
      map((res: any) => res.reports),
    );
  }

  fetchPdfReportDetail(doctorId: string, patientId: string): Observable<any> {
    return this.http.get(this.apiUrl + '/pdf_report/' + patientId).pipe(
      map((res: any) => res.pdf_reports),
    );
  }

  urlToUploadPdf(patientId: string): string {
    return this.apiUrl + '/pdf_report/' + patientId;
  }


  updateReportsList(reports: any[]): void {
    this.reports.next(reports);
  }

  fetchReportDetailById(reportId: string) {
    return this.http.get(this.apiUrl + `/report_details_by_id`, {
      params: {
        report_id: reportId
      }
    }).pipe(
      map((res: any) => res.report),
    );
  }

  fetchPatientImage(patientId: string): Observable<PatientImage[]> {
    return this.http.get(this.apiUrl + '/patients/' + patientId + '/images').pipe(
      map((res: any) => res),
    );

  }

  updateReport(reportId: string, form: any) {
    return this.http.patch(this.apiUrl + '/reports/' + reportId, form);
  }

  exportReport(data: any) {
    return this.http.post(this.apiUrl + '/generate_report', data);
  }

  deleteReport(reportId: string) {
    return this.http.delete(this.apiUrl + '/reports/' + reportId);
  }

  deleteReportRequest(id: string): Observable<any> {
    return this.http.delete(this.apiUrl + '/report_requests/' + id)
      .pipe(map((res: any) => res));
  }
}
